import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  Science as DrillIcon,
  LocalFireDepartment as FireIcon,
  Thunderstorm as WeatherIcon,
  MedicalServices as MedicalIcon,
  Shield as SecurityIcon,
  Warning as HazmatIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { appColors } from '../../Config/Theme';
import { IncidentType } from '../../Types/enums';

export interface IncidentTypeOption {
  value: string;
  label: string;
  description: string;
  icon: React.ReactElement;
  color: string;
}

export const INCIDENT_TYPES: IncidentTypeOption[] = [
  { value: IncidentType.Drill, label: 'Drill', description: 'Practice drill', icon: <DrillIcon sx={{ fontSize: 22 }} />, color: appColors.primary.main },
  { value: IncidentType.Fire, label: 'Fire', description: 'Fire emergency', icon: <FireIcon sx={{ fontSize: 22 }} />, color: appColors.status.warning },
  { value: IncidentType.Weather, label: 'Weather', description: 'Severe weather event', icon: <WeatherIcon sx={{ fontSize: 22 }} />, color: '#5C6BC0' },
  { value: IncidentType.Medical, label: 'Medical', description: 'Medical emergency', icon: <MedicalIcon sx={{ fontSize: 22 }} />, color: appColors.status.error },
  { value: IncidentType.Security, label: 'Security', description: 'Active threat or lockdown', icon: <SecurityIcon sx={{ fontSize: 22 }} />, color: appColors.status.errorDark },
  { value: IncidentType.Hazmat, label: 'Hazmat', description: 'Chemical spill or hazardous material', icon: <HazmatIcon sx={{ fontSize: 22 }} />, color: '#7B1FA2' },
];

interface IncidentTypeSelectorProps {
  value: string;
  onChange: (value: string) => void;
}

const IncidentTypeSelector: React.FC<IncidentTypeSelectorProps> = ({ value, onChange }) => (
  <Box
    role="radiogroup"
    aria-label="Incident type"
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
      gap: 2,
    }}
  >
    {INCIDENT_TYPES.map((t) => {
      const selected = value === t.value;
      return (
        <Box
          key={t.value}
          onClick={() => onChange(t.value)}
          role="radio"
          aria-checked={selected}
          aria-label={`Select ${t.label} incident type`}
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onChange(t.value); } }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            borderRadius: '12px',
            border: selected ? `2px solid ${appColors.primary.main}` : `1px solid ${appColors.border.default}`,
            bgcolor: selected ? 'rgba(46, 139, 132, 0.06)' : appColors.surface.card,
            px: 2,
            py: 1.5,
            cursor: 'pointer',
            position: 'relative',
            transition: 'border-color 0.15s, background-color 0.15s, box-shadow 0.15s',
            boxShadow: selected ? `0 0 0 1px ${appColors.primary.main}` : 'none',
            '&:hover': {
              borderColor: selected ? appColors.primary.main : appColors.border.hover,
              bgcolor: selected ? 'rgba(46, 139, 132, 0.09)' : 'rgba(0,0,0,0.02)',
            },
            '&:focus-visible': { outline: `2px solid ${appColors.primary.main}`, outlineOffset: 2 },
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              bgcolor: selected ? `${t.color}18` : `${t.color}10`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: t.color,
              flexShrink: 0,
            }}
          >
            {t.icon}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: appColors.text.heading, lineHeight: 1.3 }}>
              {t.label}
            </Typography>
            <Typography variant="caption" sx={{ color: appColors.text.caption, lineHeight: 1.3 }}>
              {t.description}
            </Typography>
          </Box>
          {selected && (
            <CheckIcon sx={{ fontSize: 22, color: appColors.primary.main, flexShrink: 0 }} />
          )}
        </Box>
      );
    })}
  </Box>
);

export default IncidentTypeSelector;
